/**
 * DynamicsAIEditor
 *
 * Workflow node editor for the "Dynamics 365 AI" action.
 *
 * Architecture:
 *   ① AI Config         — which LLM + system prompt (tells the AI what to do in D365)
 *   ② Dynamics Org      — which D365 credentials to use (set up in Integrations → Dynamics)
 *   ③ Built-in Tools    — Velaro connects to D365 directly. No MCP server needed.
 *                         Tools are fetched from /Dynamics/Capabilities — when a dev
 *                         adds a new endpoint, it appears here automatically.
 *   ④ Scenario Prompts  — copy/paste snippets for the AI Config's system prompt
 *   ⑤ Specialists       — optional: other AI Configs this AI can delegate sub-topics to
 *
 * Recommended pattern: use a general-purpose AI as the entry point, configure it to
 * delegate to this Dynamics AI specialist when CRM-related topics come up. The specialist
 * has the D365 tools and handles those topics, then returns control to the main AI.
 */
import * as React from "react";
import { useState, useEffect } from "react";
import { FormGroup } from "../RightAsideHelpers";
import Select from "@/components/Select";
import { WorkflowNode } from "../../types";
import useAIConfigs from "@/hooks/useAIConfigs";
import useDynamicsConfigs from "@/hooks/useDynamicsConfigs";
import useMcpServers from "@/hooks/useMcpServers";
import useApi from "@/hooks/useApi";
import AISpecialistSection from "./AISpecialistSection";
import AIJumpTargetSection from "./AIJumpTargetSection";
import AIEscalationSection from "./AIEscalationSection";
import AIContextInspector from "./AIContextInspector";

interface Props {
  node: WorkflowNode;
  onUpdate: (node: Partial<WorkflowNode>) => void;
}

interface DynamicsCapability {
  name: string;
  label: string;
  description: string;
  parameters: string[];
  category: string;
  implemented: boolean;
}

/** All scenarios with prompts — updated here when new tools are added */
const DYNAMICS_SCENARIOS = [
  {
    id: "contact_lookup",
    label: "Contact Lookup & Identification",
    description: "Identify the visitor by looking up their contact record before doing anything else.",
    tools: ["dynamics365_search_contact", "dynamics365_get_contact"],
    prompt: `When the conversation starts or when you need to identify the visitor, use dynamics365_search_contact with their email address. If a match is found, greet them by name and acknowledge their company. Use dynamics365_get_contact to get their full record including any open support cases. If no contact is found, offer to create one. Always ask for email first — it is the most reliable search key.`
  },
  {
    id: "lead_qualification",
    label: "Lead Qualification & Creation",
    description: "Collect visitor details conversationally and create a qualified CRM lead.",
    tools: ["dynamics365_search_contact", "dynamics365_create_lead", "dynamics365_push_note"],
    prompt: `Your goal is to qualify this visitor as a sales lead. Collect the following naturally — do not list them all at once: first name, last name, business email, company name, phone number, and their main need or question. Before creating a lead, call dynamics365_search_contact to check if they already exist as a contact — avoid creating duplicates. Once you have all required details, call dynamics365_create_lead. Then call dynamics365_push_note to attach a concise conversation summary to the lead record. Confirm to the visitor that your team will follow up within 1 business day.`
  },
  {
    id: "support_intake",
    label: "Support Case Intake",
    description: "Look up the visitor's contact, log their issue, and create a support case.",
    tools: ["dynamics365_search_contact", "dynamics365_create_case", "dynamics365_push_note"],
    prompt: `When the visitor describes a problem or support request, first call dynamics365_search_contact using their email to find their contact record. If found, acknowledge who they are. Ask them to describe their issue in one or two sentences and rate its urgency (low / medium / urgent). Call dynamics365_create_case to log the issue linked to their contact record (caseTypeCode: 2 for Problem). Then call dynamics365_push_note to attach the full conversation context to their contact. Inform the visitor that a ticket has been created and they will receive a follow-up email. If the issue is urgent or complex, escalate to a human agent.`
  },
  {
    id: "account_research",
    label: "Account Research",
    description: "Look up a company account and its open opportunities before a sales conversation.",
    tools: ["dynamics365_search_account", "dynamics365_search_opportunity", "dynamics365_push_note"],
    prompt: `When asked about a company or account, call dynamics365_search_account with the company name. Share relevant details: industry, website, and employee count. Then call dynamics365_search_opportunity to check for any open sales opportunities linked to this account. If active opportunities exist, mention that your team is already in conversation with them. Use dynamics365_push_note to log this research interaction on the account record. Never share financial data (revenue, credit terms) with visitors — only internal team members may access that information.`
  },
  {
    id: "contact_update",
    label: "Update Contact Information",
    description: "Visitor wants to update their phone, email, or other contact details on file.",
    tools: ["dynamics365_search_contact", "dynamics365_update_contact"],
    prompt: `When the visitor wants to update their contact information, first call dynamics365_search_contact using their current email to find their record. Confirm their identity by asking them to verify their name and company. Once confirmed, ask what they would like to update (phone, email, job title, etc.) and call dynamics365_update_contact with only the changed fields. Confirm the update to the visitor and summarize what was changed.`
  },
  {
    id: "case_status",
    label: "Check Support Case Status",
    description: "Visitor wants to know the status of an existing support case.",
    tools: ["dynamics365_search_contact", "dynamics365_search_cases"],
    prompt: `When the visitor asks about a support case or ticket, first call dynamics365_search_contact using their email to identify them. Then call dynamics365_search_cases to find their active cases. Present the case title, current status, and priority. If the case is escalated or past its expected resolution date, acknowledge this and offer to connect them with a support specialist. If they need to add more information to the case, use dynamics365_push_note to attach it.`
  },
  {
    id: "conversation_summary",
    label: "Push Conversation Summary to CRM",
    description: "At conversation end, attach a summary note to the contact or account record.",
    tools: ["dynamics365_search_contact", "dynamics365_push_note"],
    prompt: `At the end of every conversation, call dynamics365_search_contact using the visitor's email address. If found, call dynamics365_push_note with a concise summary (150 words max) including: the visitor's main question or request, the outcome, and any follow-up items. Format the note title as "Velaro Chat — [today's date]". If no contact is found, search for their account and attach the note there instead. Confirm to the visitor that the conversation has been logged.`
  }
];

/** Generate a base system prompt from the list of selected tool names */
function generateBasePrompt(capabilities: DynamicsCapability[], selectedTools: string[]): string {
  const selected = capabilities.filter(c => selectedTools.includes(c.name) && c.implemented);
  if (selected.length === 0) return "";
  const toolList = selected.map(c => `- ${c.name}: ${c.description}`).join("\n");
  return `You have access to the following Dynamics 365 tools. Use them when the visitor's request requires CRM data or actions:\n\n${toolList}\n\nAlways verify the visitor's identity via email before taking any write actions. Never share another customer's data.`;
}

export default function DynamicsAIEditor({ node, onUpdate }: Props) {
  const api = useApi();
  const { aiConfigurations } = useAIConfigs();
  const { items: dynamicsConfigs, loading: dynLoading } = useDynamicsConfigs();
  const { items: mcpServers } = useMcpServers();

  const [capabilities, setCapabilities] = useState<DynamicsCapability[]>([]);
  const [selectedTools, setSelectedTools] = useState<string[]>(
    Array.isArray((node as any).dynamicsToolNames) ? (node as any).dynamicsToolNames : []
  );
  const [copiedScenario, setCopiedScenario] = useState<string | null>(null);
  const [expandedScenario, setExpandedScenario] = useState<string | null>(null);
  const [copiedBasePrompt, setCopiedBasePrompt] = useState(false);
  const [showMcp, setShowMcp] = useState(false);

  // Load capabilities from backend — auto-updates when devs add new endpoints
  useEffect(() => {
    api.admin.get("Dynamics/Capabilities")
      .then(r => r.json())
      .then(data => Array.isArray(data) ? setCapabilities(data) : [])
      .catch(() => setCapabilities([]));
  }, [api]);

  // Default: select all implemented tools when first configured
  useEffect(() => {
    if (capabilities.length > 0 && selectedTools.length === 0) {
      const all = capabilities.filter(c => c.implemented).map(c => c.name);
      setSelectedTools(all);
      onUpdate({ dynamicsToolNames: all } as any);
    }
  }, [capabilities]);

  const aiOptions = aiConfigurations.map(ai => ({ value: ai.id, label: ai.name }));
  const dynOptions = dynamicsConfigs.filter(d => d.enabled).map(d => ({
    value: d.id,
    label: d.displayName || d.orgName || `Config #${d.id}`
  }));
  const allDynOptions = dynamicsConfigs.map(d => ({
    value: d.id,
    label: (d.displayName || d.orgName || `Config #${d.id}`) + (d.enabled ? "" : " (disabled)")
  }));

  const selectedAiConfig = aiConfigurations.find(ai => ai.id === node.aiConfigId);
  const enabledMcpServers = mcpServers.filter(s => s.enabled);
  const selectedMcpIds: number[] = Array.isArray(node.mcpServerIds) ? node.mcpServerIds : [];

  // For context inspector: built-in tool names + any MCP tools
  const mcpToolNames = enabledMcpServers
    .filter(s => selectedMcpIds.includes(s.id))
    .flatMap(s => s.toolNames ?? []);
  const allToolNames = [...selectedTools, ...mcpToolNames];

  // Group capabilities by category
  const byCategory = capabilities.reduce<Record<string, DynamicsCapability[]>>((acc, cap) => {
    if (!acc[cap.category]) acc[cap.category] = [];
    acc[cap.category].push(cap);
    return acc;
  }, {});

  function toggleTool(name: string, checked: boolean) {
    const next = checked ? [...selectedTools, name] : selectedTools.filter(t => t !== name);
    setSelectedTools(next);
    onUpdate({ dynamicsToolNames: next } as any);
  }

  function toggleMcpServer(id: number, checked: boolean) {
    const next = checked ? [...selectedMcpIds, id] : selectedMcpIds.filter(x => x !== id);
    onUpdate({ mcpServerIds: next.length > 0 ? next : undefined });
  }

  async function copyScenarioPrompt(id: string, prompt: string) {
    try {
      await navigator.clipboard.writeText(prompt);
      setCopiedScenario(id);
      setTimeout(() => setCopiedScenario(null), 2000);
    } catch { /* clipboard unavailable */ }
  }

  async function copyBasePrompt() {
    const prompt = generateBasePrompt(capabilities, selectedTools);
    try {
      await navigator.clipboard.writeText(prompt);
      setCopiedBasePrompt(true);
      setTimeout(() => setCopiedBasePrompt(false), 2000);
    } catch { /* clipboard unavailable */ }
  }

  const basePrompt = generateBasePrompt(capabilities, selectedTools);

  return (
    <>
      {/* ── How it works banner ── */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2.5 mb-3">
        <p className="text-xs font-semibold text-blue-800 mb-1">How this node works</p>
        <p className="text-xs text-blue-700 leading-relaxed">
          This AI node connects directly to your Dynamics 365 org — no extra servers needed.
          Configure it as a specialist and have your main workflow AI delegate to it when
          CRM topics come up.
        </p>
        <p className="text-xs text-blue-600 mt-1.5">
          Recommended: <strong>Main AI → detects CRM question → delegates to Dynamics AI specialist → returns to main AI</strong>
        </p>
      </div>

      {/* ── Step 1: AI Config ── */}
      <FormGroup label="AI Config">
        {aiConfigurations.length === 0 ? (
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
            No AI configs found.{" "}
            <a href="/bots" className="underline font-medium">Create an AI Config first →</a>
            <p className="mt-1 text-slate-500">
              The AI Config holds the system prompt. Use the Scenario Prompts below to populate it
              with Dynamics 365 behaviors.
            </p>
          </div>
        ) : (
          <Select
            options={[{ value: 0, label: "— Select AI Config —" }, ...aiOptions]}
            value={node.aiConfigId ?? 0}
            onChange={value => onUpdate({ aiConfigId: Number(value) || undefined })}
          />
        )}
        {selectedAiConfig && !selectedAiConfig.prompt && (
          <p className="text-xs text-amber-600 mt-1">
            This AI config has no system prompt.{" "}
            <a href="/bots" className="underline">Add a prompt →</a>{" "}
            Use the Base Prompt below to get started quickly.
          </p>
        )}
      </FormGroup>

      {/* ── Step 2: Dynamics 365 Org ── */}
      <FormGroup label="Dynamics 365 Org">
        {!dynLoading && dynamicsConfigs.length === 0 ? (
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
            No Dynamics 365 org connected.{" "}
            <a href="/integrations/dynamics" className="underline font-medium">Connect a Dynamics 365 org →</a>
            <p className="mt-1 text-slate-500">
              The Dynamics config holds the OAuth credentials needed to query and write records.
            </p>
          </div>
        ) : (
          <>
            <Select
              options={[
                { value: 0, label: "— Select Dynamics 365 Org —" },
                ...(dynOptions.length > 0 ? dynOptions : allDynOptions)
              ]}
              value={node.dynamicsConfigId ?? 0}
              onChange={value => onUpdate({ dynamicsConfigId: Number(value) || undefined })}
            />
            {dynamicsConfigs.length > 0 && dynOptions.length === 0 && (
              <p className="text-xs text-amber-600 mt-1">
                All connected orgs are disabled.{" "}
                <a href="/integrations/dynamics" className="underline">Enable an org →</a>
              </p>
            )}
          </>
        )}
      </FormGroup>

      {/* ── Step 3: Built-in Tools ── */}
      <FormGroup label="Dynamics 365 Tools">
        <p className="text-xs text-slate-500 mb-2 leading-relaxed">
          Select which Dynamics operations this AI can perform. These are Velaro's built-in tools —
          no MCP server required. New tools appear here automatically as Velaro adds them.
        </p>
        {capabilities.length === 0 ? (
          <div className="text-xs text-slate-400 italic px-2 py-1">Loading tools...</div>
        ) : (
          <div className="flex flex-col gap-3">
            {Object.entries(byCategory).map(([category, caps]) => (
              <div key={category}>
                <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5">
                  {category}
                </div>
                <div className="flex flex-col gap-1">
                  {caps.map(cap => {
                    const checked = selectedTools.includes(cap.name);
                    return (
                      <label
                        key={cap.name}
                        className={`flex gap-2.5 cursor-pointer rounded border p-2 transition-colors ${
                          checked ? "border-blue-300 bg-blue-50" : "border-slate-200 bg-white hover:border-slate-300"
                        } ${!cap.implemented ? "opacity-50" : ""}`}
                      >
                        <input
                          type="checkbox"
                          className="w-4 h-4 mt-0.5 shrink-0"
                          checked={checked}
                          disabled={!cap.implemented}
                          onChange={e => toggleTool(cap.name, e.target.checked)}
                          aria-label={cap.label}
                        />
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs font-medium text-slate-700">{cap.label}</span>
                            {!cap.implemented && (
                              <span className="text-[10px] px-1 bg-slate-100 text-slate-400 rounded">coming soon</span>
                            )}
                          </div>
                          <div className="text-[10px] text-slate-400 leading-tight">{cap.description}</div>
                          <div className="font-mono text-[10px] text-slate-400 mt-0.5">{cap.name}</div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Auto-generated base system prompt */}
        {basePrompt && (
          <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-2.5">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[10px] font-semibold text-slate-600 uppercase tracking-wide">
                Generated Base Prompt
              </span>
              <button
                type="button"
                onClick={copyBasePrompt}
                className={`text-[10px] px-2 py-0.5 rounded border transition-colors ${
                  copiedBasePrompt
                    ? "border-green-300 bg-green-50 text-green-700"
                    : "border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100"
                }`}
              >
                {copiedBasePrompt ? "✓ Copied!" : "Copy"}
              </button>
            </div>
            <p className="text-[10px] text-slate-500 mb-1.5">
              Paste this into your AI Config's system prompt so the AI knows which tools it has.
              Add scenario prompts below for behavioral guidance.
            </p>
            <pre className="text-[10px] bg-slate-900 text-slate-100 rounded p-2 whitespace-pre-wrap leading-relaxed font-mono">
              {basePrompt}
            </pre>
          </div>
        )}
      </FormGroup>

      {/* ── Scenario Prompts ── */}
      <div className="mt-1">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
            Scenario Prompts
          </span>
          <span className="text-xs text-slate-400 italic">for your AI Config</span>
        </div>
        <p className="text-xs text-slate-500 mb-2 leading-relaxed">
          Copy these into your AI Config's system prompt. The AI adapts based on what the visitor
          already told it — it won't ask for info it already has.
        </p>
        <div className="flex flex-col gap-2">
          {DYNAMICS_SCENARIOS.map(scenario => {
            const isExpanded = expandedScenario === scenario.id;
            const isCopied = copiedScenario === scenario.id;
            const hasAllTools = scenario.tools.every(t => selectedTools.includes(t));
            return (
              <div key={scenario.id} className="border border-slate-200 rounded bg-white overflow-hidden">
                <button
                  type="button"
                  onClick={() => setExpandedScenario(isExpanded ? null : scenario.id)}
                  className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-slate-50 transition-colors"
                  aria-expanded={isExpanded}
                >
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-slate-800">{scenario.label}</span>
                      {!hasAllTools && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-600 border border-amber-200">
                          enable {scenario.tools.filter(t => !selectedTools.includes(t)).length} more tools
                        </span>
                      )}
                    </div>
                    {!isExpanded && (
                      <p className="text-xs text-slate-400 mt-0.5">{scenario.description}</p>
                    )}
                  </div>
                  <span className="text-slate-400 text-xs ml-2 shrink-0">{isExpanded ? "▲" : "▼"}</span>
                </button>
                {isExpanded && (
                  <div className="px-3 pb-3">
                    <p className="text-xs text-slate-500 mb-1.5">{scenario.description}</p>
                    <div className="flex flex-wrap gap-1 mb-2">
                      {scenario.tools.map(t => (
                        <span key={t} className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${
                          selectedTools.includes(t) ? "bg-green-100 text-green-700" : "bg-amber-50 text-amber-600"
                        }`}>{t}</span>
                      ))}
                    </div>
                    <pre className="text-xs bg-slate-900 text-slate-100 rounded p-2.5 whitespace-pre-wrap leading-relaxed font-mono mb-2">
                      {scenario.prompt}
                    </pre>
                    <button
                      type="button"
                      onClick={() => copyScenarioPrompt(scenario.id, scenario.prompt)}
                      className={`text-xs px-2.5 py-1 rounded border transition-colors ${
                        isCopied
                          ? "border-green-300 bg-green-50 text-green-700"
                          : "border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100"
                      }`}
                    >
                      {isCopied ? "✓ Copied!" : "Copy prompt snippet"}
                    </button>
                    <p className="text-xs text-slate-400 mt-1.5">
                      Paste into your AI Config's system prompt, then customize for your business.
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Optional: Custom MCP Tools (Advanced) ── */}
      <div className="mt-3 border border-slate-200 rounded overflow-hidden">
        <button
          type="button"
          onClick={() => setShowMcp(!showMcp)}
          className="w-full flex items-center justify-between px-3 py-2 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
          aria-expanded={showMcp}
        >
          <div>
            <span className="text-xs font-medium text-slate-700">Custom MCP Tools (Advanced)</span>
            <p className="text-[10px] text-slate-400 mt-0.5">
              Add your own API tools via an MCP server. Optional — only needed for operations beyond the built-in tools above.
            </p>
          </div>
          <span className="text-slate-400 text-xs ml-2 shrink-0">{showMcp ? "▲" : "▼"}</span>
        </button>
        {showMcp && (
          <div className="p-3">
            {enabledMcpServers.length === 0 ? (
              <p className="text-xs text-slate-500">
                No MCP servers configured.{" "}
                <a href="/integrations/mcp" className="text-blue-600 underline">Add one →</a>
              </p>
            ) : (
              <div className="flex flex-col gap-2">
                {enabledMcpServers.map(server => {
                  const checked = selectedMcpIds.includes(server.id);
                  return (
                    <label key={server.id} className={`flex gap-2.5 cursor-pointer rounded border p-2.5 transition-colors ${checked ? "border-blue-300 bg-blue-50" : "border-slate-200 bg-white hover:border-slate-300"}`}>
                      <input
                        type="checkbox"
                        className="w-4 h-4 mt-0.5 shrink-0"
                        checked={checked}
                        onChange={e => toggleMcpServer(server.id, e.target.checked)}
                        aria-label={`Enable MCP server: ${server.name}`}
                      />
                      <div>
                        <span className="text-sm font-medium">{server.name}</span>
                        {server.description && <div className="text-xs text-slate-500">{server.description}</div>}
                        {server.toolNames.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-0.5">
                            {server.toolNames.slice(0, 6).map(t => (
                              <span key={t} className="text-xs text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded font-mono">{t}</span>
                            ))}
                            {server.toolNames.length > 6 && <span className="text-xs text-slate-400">+{server.toolNames.length - 6} more</span>}
                          </div>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Context Inspector (debug) ── */}
      <AIContextInspector mcpToolNames={allToolNames} />

      {/* ── Specialist Agents (Orchestration) ── */}
      <div className="mt-4 pt-3 border-t border-slate-200">
        <AISpecialistSection
          node={node}
          onUpdate={onUpdate}
          aiConfigurations={aiConfigurations}
          enabledMcpServers={enabledMcpServers}
        />
      </div>

      {/* ── Escalation & Handoff ── */}
      <div className="mt-2 pt-3 border-t border-slate-200">
        <AIEscalationSection node={node} onUpdate={onUpdate} />
      </div>

      {/* ── Jump to Workflow Section (advanced / custom) ── */}
      <div className="mt-2 pt-3 border-t border-slate-200">
        <AIJumpTargetSection node={node} onUpdate={onUpdate} />
      </div>
    </>
  );
}
